import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AlertboxComponent } from '@src/app/helpers/alertbox/alertbox.component';
import { LoadconfirmComponent } from '@src/app/components/utilities/loadconfirm/loadconfirm.component';
import { TransPipe } from '@src/app/helpers/trans/trans.pipe';
import { Orgfile } from './model/orgfile';
import { FileuploadService } from './service/fileupload.service';
import { trigger, style, animate, transition } from '@angular/animations';
import { fileBaseName } from '@src/app/helpers/fileName.helper';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-fileupload',
  standalone: true,
  imports: [CommonModule, AlertboxComponent, LoadconfirmComponent, TransPipe],
  templateUrl: './fileupload.component.html',
  styleUrls: ['./fileupload.component.css'],
  animations: [
    trigger('inbgOutAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('0.2s ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('0.2s ease-in', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class FileuploadComponent implements OnInit, OnChanges {
  selectedFiles: FileList | null = null;
  selectedFileName: string | null = null;
  // Selection already handed to onDocumentSubmit; selecting a file auto-uploads,
  // so the modal's save button must not upload the same selection again.
  private submittedFiles: FileList | null = null;
  showalert: boolean;
  dataloaded: boolean;
  @Input() orgfile: Orgfile;
  @Input() organizationId: string;
  @Input() projectId: string;
  @Input() index: string;
  @Input() taskInputName: string;
  @Input() filetype: string;
  @Input() customerId: string;
  @Input() visibility: string;
  @Input() modal: boolean = true;
  @Input() filename: string;
  @Input() downloadtoggle: boolean;
  showSpinner: boolean;
  uploaddisabled: boolean;
  fileUploadedtoggle: boolean;
  deletecontrol: boolean;
  files: File[] = [];
  showdeletealert: boolean;
  @Output() filelistUpdated = new EventEmitter(true);
  @Output() fileUploaded = new EventEmitter<Orgfile>(true);
  @Output() fileDeleted = new EventEmitter<string>(true); // Emit the taskInputName when file is deleted
  filedeleteUploadedtoggle: boolean;

  constructor(
    private orgFileService: FileuploadService,
    private router: Router,
    private http: HttpClient
  ) { }

  ngOnInit() {
    const shouldLookupExisting = this.filetype === 'task' && !!(this.taskInputName && this.organizationId && this.index);

    if (shouldLookupExisting) {
      // try to get file from storage
      this.orgFileService.getTaskFiles(this.organizationId, this.index.split('_')[0]).subscribe(files => {
        const matchedFile = files.find(f => f.taskInputName === this.taskInputName);
        if (matchedFile) {
          this.orgfile = matchedFile;
          this.selectedFileName = fileBaseName(this.orgfile.filename).split('_').slice(1).join('_');
          this.downloadtoggle = true;
          this.filelistUpdated.emit(true);
          // Emit fileUploaded event for existing files so they get counted
          this.fileUploaded.emit(this.orgfile);
          this.fileUploadedtoggle = true;
          this.dataloaded = true;
        } else {
          this.orgfile = new Orgfile();
          this.filelistUpdated.emit(true);
          this.dataloaded = true;
        }
      });
    } else {
      // Only reset if orgfile wasn't already provided via input
      if (!this.orgfile || Object.keys(this.orgfile).length === 0) {
        this.orgfile = new Orgfile();
      }
      this.filelistUpdated.emit(true);
      this.dataloaded = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('[FileuploadComponent] ngOnChanges:', changes);
    
    // Update internal orgfile if input changed
    if (changes['orgfile'] && changes['orgfile'].currentValue) {
      console.log('[FileuploadComponent] orgfile input changed to:', changes['orgfile'].currentValue);
      this.orgfile = changes['orgfile'].currentValue;
      this.selectedFileName = this.extractDisplayName(changes['orgfile'].currentValue as Orgfile);
      this.fileUploadedtoggle = true;
      this.downloadtoggle = true;
      this.dataloaded = true;
    }

    if ((changes['taskInputName'] && !changes['taskInputName'].firstChange) ||
        (changes['index'] && !changes['index'].firstChange)) {
      // Reset component state when task changes
      this.resetComponentState();
      this.ngOnInit();
    }
  }

  private extractDisplayName(file: Orgfile): string | null {
    if (!file?.filename) {
      return null;
    }
    const baseName = fileBaseName(file.filename);
    return baseName.split('_').slice(1).join('_') || baseName;
  }

  private resetComponentState() {
    // Reset all component state to initial values
    this.orgfile = new Orgfile();
    this.selectedFileName = null;
    this.selectedFiles = null;
    this.submittedFiles = null;
    this.fileUploadedtoggle = false;
    this.uploaddisabled = false;
    this.deletecontrol = false;
    this.dataloaded = false;
    this.showSpinner = false;
    this.showalert = false;
    this.showdeletealert = false;
    this.filedeleteUploadedtoggle = false;
  }

  getSignedUrl(file: Orgfile): void {
    console.log('[FileuploadComponent] getSignedUrl called with file:', file);
    console.log('[FileuploadComponent] file.filename:', file.filename);
    if (!file?.filename) {
      console.error('[FileuploadComponent] Cannot get signed URL: filename is missing', file);
      return;
    }
    this.orgFileService.getSignedUrl(file.filename).pipe(untilDestroyed(this)).subscribe(url => {
      console.log('[FileuploadComponent] Signed URL received:', url);
      this.http.get(url.url, { responseType: 'blob' }).pipe(untilDestroyed(this)).subscribe(blob => {
        // Create an anchor element and trigger the download
        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = fileBaseName(file.filename);
        link.click();
        URL.revokeObjectURL(objectUrl); // Clean up
      });
    });
  }

  deleteFile(ofile?: Orgfile) {
    this.orgFileService.deleteFile(ofile).pipe(untilDestroyed(this)).subscribe(() => {
      this.orgfile = new Orgfile();
      this.selectedFileName = null;
      this.uploaddisabled = false;
      this.fileUploadedtoggle = false;
      this.deletecontrol = false;
      
      // Emit both the old null event (for backward compatibility) and the new deletion event
      this.fileUploaded.emit(null);
      this.fileDeleted.emit(this.taskInputName);
      
      this.showdeletesuccesUpload();
    });
  }

  onDocumentSubmit() {
    if (!this.selectedFiles || this.selectedFiles.length === 0 || !this.selectedFileName) {
      return;
    }
    if (this.selectedFiles === this.submittedFiles) {
      return;
    }
    this.submittedFiles = this.selectedFiles;
    this.showSpinner = true;
    //console.log('Org id in task',this.organizationId);
    //console.log('project id in task',this.projectId);
    //console.log('index in task',this.index);
    //console.log('file type in task',this.filetype);
    //console.log('customer id in task',this.customerId);
    const filename = `${localStorage.getItem('user_sub')}_${this.selectedFileName}`;
    const file = this.selectedFiles.item(0);
    if (!file) {
      this.showSpinner = false;
      this.submittedFiles = null;
      return;
    }
    let fileContentType = file.type;
    //console.log('File content type: '+fileContentType);
    this.orgFileService.getSignedUploadUrl(filename, fileContentType, this.organizationId).pipe(untilDestroyed(this)).subscribe({
      next: SignedUploadUrl => {
        //console.log('Signed upload url: '+SignedUploadUrl.url);
        this.orgFileService.uploadFile(file, SignedUploadUrl.url).pipe(untilDestroyed(this)).subscribe({
          next: resp => {
            const objectKey = SignedUploadUrl.key;
            this.orgfile.filename = objectKey;
            this.orgfile.organizationId = this.organizationId;
            this.orgfile.projectId = this.projectId;
            this.orgfile.uploaddate = new Date();
            this.orgfile.filetype = this.filetype;
            this.orgfile.customerId = this.customerId;
            this.orgfile.visibility = this.visibility;
            if (this.orgfile.filetype === 'task') {
              this.orgfile.taskId = this.index.split('_')[0];
              this.orgfile.taskInputName = this.taskInputName;
            }
            this.orgfile.uploadeby = localStorage.getItem('user_sub');
            this.orgFileService.addorgFile(this.orgfile).pipe(untilDestroyed(this)).subscribe({
              next: pi => {
                //console.log('return from file upload',pi);
                if (pi) {
                  this.orgfile.fileId = pi;
                  //this.orgfile = pi;
                  this.orgFileService.getorgFileUrl(objectKey).pipe(untilDestroyed(this)).subscribe({
                    next: url => {
                      this.orgfile.fileurl = url.fileurl;
                      //console.log('File added',JSON.stringify(this.orgfile));
                      this.showSpinner = false;
                      this.showsuccesUpload();
                      this.fileUploaded.emit(this.orgfile);
                    },
                    error: () => this.onUploadFailed(),
                  });
                } else {
                  this.onUploadFailed();
                }
              },
              error: () => this.onUploadFailed(),
            });
          },
          error: () => this.onUploadFailed(),
        });
      },
      error: () => this.onUploadFailed(),
    });
  }

  private onUploadFailed() {
    this.submittedFiles = null;
    this.showSpinner = false;
    this.uploaddisabled = false;
  }

  deletetoggler(boolean) {
    this.deletecontrol = boolean;
  }

  selectFile(event) {
    this.selectedFiles = event.target.files;
    this.selectedFileName = this.selectedFiles.item(0).name;
    if (this.selectedFiles != null) {
      this.uploaddisabled = true;
      this.submittedFiles = null;
      this.onDocumentSubmit();
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files) {
      this.selectedFiles = (event.dataTransfer.files);
      this.selectedFileName = this.selectedFiles.item(0).name;
      this.uploaddisabled = true;
      this.submittedFiles = null;
      this.onDocumentSubmit();
    }
  }

  showsuccesUpload() {
    this.showalert = true;
    this.fileUploadedtoggle = true;
    setTimeout(() => {
      this.showalert = false;
    }, 2000);
  }

  showdeletesuccesUpload() {
    this.showdeletealert = true;
    this.filedeleteUploadedtoggle = true;
    setTimeout(() => {
      this.showdeletealert = false;
    }, 2000);
  }
}
